import { ProcedimentoSelectModel } from 'components/form/field/select/ProcedimentoSelectField'
import {
  isProcedimentoSisab,
  shouldUseProcedimentoReferencia,
} from 'components/form/field/select/ProcedimentoSelectField/ProcedimentoSelectFieldItem'
import { SemanasDiasFieldModel } from 'components/form/field/SemanasDiasField'
import {
  FichaComplementarZikaMicrocefaliaResultadoExameEnum,
  OpcoesResultadoExameQualitativoEnum,
  ResultadoExamePreNatalInput,
} from 'graphql/types.generated'
import { isUndefinedOrNull } from 'util/checks'
import { formatDate } from 'util/date/formatDate'
import { formatNumber } from 'util/number'

import { convertResultadoExameToResultadoExamePreNatalInput } from '../../aside/pre-natal/converter-acompanhamentoPreNatal'
import { ConverterResultadoExameType } from '../converter-objetivo'
import {
  AllResultadoEspecificoValueType,
  ExameQueryModel,
  examesEspecificosKeys,
  FactoryResultadoExameEspecificoFieldProps,
  FactoryResultadoExameEspecificoModel,
  FactoryResultadoExameEspecificoProperties,
  FactoryResultadoExameEspecificoViewProps,
  ResultadoEspecificoPrenatal,
  ResultadoExameEspecificoModel,
  resultadosExamesEspecificosFactory,
  ResultadosExamesModel,
} from './model-resultadosExames'

const RESULTADOS_EXAMES_PREFIX = 'id'

export const formatResultadoExameEspecificoDecimal = (
  value: ResultadoExameEspecificoModel,
  exame: ExameQueryModel
): ResultadoExameEspecificoModel => {
  const { resultado, ...prevValue } = value
  const { precision, maxLength } = getResultadoExameEspecificoProperties(exame)

  const tmpValue = formatNumber(resultado, precision).slice(0, maxLength)
  const lastChar = tmpValue.slice(-1)
  const newValue = lastChar === ',' ? tmpValue.slice(0, -1) : tmpValue

  return {
    ...prevValue,
    resultado: newValue as AllResultadoEspecificoValueType,
  }
}

export const mapResultadosPuericulturaEnumLabel: Record<FichaComplementarZikaMicrocefaliaResultadoExameEnum, string> = {
  ORELHINHA_FALHOU: 'Falhou',
  ORELHINHA_PASSOU: 'Passou',
  TRANSFONTANELA_NORMAL: 'Normal',
  TRANSFONTANELA_SUGESTIVO_INFECCAO_CONGENITA: 'Sugestivo de infecção congênita',
  TRANSFONTANELA_OUTRAS_ALTERACOES: 'Outras alterações',
  TRANSFONTANELA_INDETERMINADO: 'Indeterminado',
  TOMOGRAFIA_NORMAL: 'Normal',
  TOMOGRAFIA_SUGESTIVO_INFECCAO_CONGENITA: 'Sugestivo de infecção congênita',
  TOMOGRAFIA_OUTRAS_ALTERACOES: 'Outras alterações',
  TOMOGRAFIA_INDETERMINADO: 'Indeterminado',
  RESSONANCIA_NORMAL: 'Normal',
  RESSONANCIA_SUGESTIVO_INFECCAO_CONGENITA: 'Sugestivo de infecção congênita',
  RESSONANCIA_OUTRAS_ALTERACOES: 'Outras alterações',
  RESSONANCIA_INDETERMINADO: 'Indeterminado',
  FUNDO_OLHO_NORMAL: 'Normal',
  FUNDO_OLHO_ALTERADO: 'Alterado',
  DUVIDOSO_OU_AUSENTE: 'Duvidoso ou ausente',
  PRESENTE_BILATERAL: 'Presente bilateral',
}

export const mapResultadosQualitativosEnumLabel: Record<OpcoesResultadoExameQualitativoEnum, string> = {
  PROVA_LACO_POSITIVO: 'Positivo',
  PROVA_LACO_NEGATIVO: 'Negativo',
  PROVA_LACO_INCONCLUSIVO: 'Inconclusivo',
}

export const isExameEspecifico = (exame: ExameQueryModel): boolean => {
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  return examesEspecificosKeys.includes(codigo)
}

export const showCodigoProcedimento = (codigo: string): string => {
  return isProcedimentoSisab(codigo) ? 'CDS' : codigo
}

export const getResultadoExameEspecificoProperties = (
  exame: ExameQueryModel
): FactoryResultadoExameEspecificoProperties => {
  const factory = resultadosExamesEspecificosFactory()
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  return factory?.[codigo]?.properties || {}
}

export const getResultadoExameEspecificoField = (
  exame: ExameQueryModel
): React.ComponentType<FactoryResultadoExameEspecificoFieldProps> => {
  const factory = resultadosExamesEspecificosFactory()
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  return factory?.[codigo]?.components?.ResultadoField
}

export const getResultadoExameEspecificoView = (
  exame: ExameQueryModel
): React.ComponentType<FactoryResultadoExameEspecificoViewProps> => {
  const factory = resultadosExamesEspecificosFactory()
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  return factory?.[codigo]?.components?.ResultadoView
}

export const getResultadoExameEspecificoValidator = (
  exame: ExameQueryModel
): FactoryResultadoExameEspecificoModel['validator'] => {
  const factory = resultadosExamesEspecificosFactory()
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  return factory?.[codigo]?.validator
}

export const getResultadoExameEspecificoConverterToInput = (
  exame: ExameQueryModel
): FactoryResultadoExameEspecificoModel['converterToInput'] => {
  const factory = resultadosExamesEspecificosFactory()
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  return factory?.[codigo]?.converterToInput
}

export const getResultadoExameEspecificoConverterToModel = (
  exame: ExameQueryModel
): FactoryResultadoExameEspecificoModel['converterValueToModel'] => {
  const factory = resultadosExamesEspecificosFactory()
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  return factory?.[codigo]?.converterValueToModel
}

export const getResultadoExameEspecificoFormatOnSave = (
  exame: ExameQueryModel
): FactoryResultadoExameEspecificoModel['formatOnSave'] => {
  const factory = resultadosExamesEspecificosFactory()
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  return factory?.[codigo]?.formatOnSave
}

export const getResultadoExameEspecificoExtractValueFromQuery = (
  exame: ExameQueryModel
): FactoryResultadoExameEspecificoModel['extractValueFromQuery'] => {
  const factory = resultadosExamesEspecificosFactory()
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  return factory?.[codigo]?.extractValueFromQuery
}

export const getResultadoExameEspecificoPuericulturaLabel = (
  value: FichaComplementarZikaMicrocefaliaResultadoExameEnum
): string => {
  return mapResultadosPuericulturaEnumLabel[value]
}

export const getResultadoExameQualitativoLabel = (value: OpcoesResultadoExameQualitativoEnum): string => {
  return mapResultadosQualitativosEnumLabel[value]
}

export const addPrefix = (value: ID): string => RESULTADOS_EXAMES_PREFIX + value
export const removePrefix = (value: string): ID => value.replace(RESULTADOS_EXAMES_PREFIX, '')

export const isResultadoExamePreNatal = (resultadoExame: ResultadoExameEspecificoModel): boolean => {
  const resultadoPreNatal = resultadoExame?.resultado as ResultadoEspecificoPrenatal
  return !isUndefinedOrNull(resultadoPreNatal?.dpp) || !isUndefinedOrNull(resultadoPreNatal?.idadeGestacional)
}

export const filterResultadosExamesAndConvertToExamePreNatalInput = (
  resultadosExames: ResultadosExamesModel,
  filter: (resultado: ResultadoExameEspecificoModel) => boolean
): ResultadoExamePreNatalInput[] => {
  const { resultadosComSolicitacao, resultadosSemSolicitacao } = resultadosExames

  const resultadosComSolicitacaoFiltradosInput = resultadosComSolicitacao
    ? Object.entries(resultadosComSolicitacao)
        .filter(([_, value]) => filter(value))
        .map(([_id, value]) => convertResultadoExameToResultadoExamePreNatalInput(value as ConverterResultadoExameType))
    : []

  const resultadosSemSolicitacaoFiltradosInput = resultadosSemSolicitacao
    ? Object.values(resultadosSemSolicitacao)
        .filter(filter)
        .map((value) => convertResultadoExameToResultadoExamePreNatalInput(value))
    : []

  return [...resultadosComSolicitacaoFiltradosInput, ...resultadosSemSolicitacaoFiltradosInput]
}

export const PROCEDIMENTOS_ABEX_EXCEPTION_CODES: Set<string> = new Set(['ABEX012', 'ABEX013', 'ABEX022'])

export const nomeCodigoProcedimentoRef = (exame: ProcedimentoSelectModel): [string, string] => {
  if (PROCEDIMENTOS_ABEX_EXCEPTION_CODES.has(exame.codigo)) {
    return [exame.descricao, 'CDS']
  }

  const shouldUseProcedimentoRef = shouldUseProcedimentoReferencia(exame.codigo)
  const codigo = shouldUseProcedimentoRef ? exame.procedimentoReferencia?.codigo : exame.codigo
  const nome = shouldUseProcedimentoRef ? exame.procedimentoReferencia?.descricao : exame.descricao

  return [nome, codigo]
}

export const showCodigoProcedimentoRef = (exame: ProcedimentoSelectModel, ocultaCodigo: boolean = false): string => {
  const [nome, codigo] = nomeCodigoProcedimentoRef(exame)

  return `${nome}${ocultaCodigo ? '' : ` - ${codigo}`}`
}

export const mountIdadeGestacionalValue = ({
  dpp,
  idadeGestacional,
}: {
  dpp: LocalDate
  idadeGestacional: SemanasDiasFieldModel
}): string => {
  let { semanas, dias } = idadeGestacional || {}

  let hasSemanas = !isUndefinedOrNull(semanas)
  let hasDias = !isUndefinedOrNull(dias)

  if (typeof semanas === 'string') {
    try {
      semanas = Number.parseInt(semanas)
    } catch {
      hasSemanas = false
    }
  }

  if (typeof dias === 'string') {
    try {
      dias = dias === '' ? 0 : Number.parseInt(dias)
    } catch {
      hasDias = false
    }
  }

  const separator = dpp && hasSemanas ? ' - ' : ''
  const diasFormatted = hasDias ? `e ${'dia'.pluralizeAndConcatValue(dias)}` : 'e 0 dia'
  const semanasAndDiasFormatted = hasSemanas
    ? `Idade gestacional eco ${'semana'.pluralizeAndConcatValue(semanas)} ${diasFormatted}`
    : ''
  const ddpFormatted = dpp ? `DPP eco ${formatDate(dpp)}` : ''

  return `${semanasAndDiasFormatted}${separator}${ddpFormatted}`
}
